import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, TOKEN_KEY } from "@/lib/handball";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = not loaded, false = anonymous, {} = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.debug("[auth] /auth/me failed, clearing token", err?.response?.status);
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((token, userData) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }, []);

  const updateStatsConfig = useCallback(async (cfg) => {
    const { data } = await api.put("/user/stats-config", cfg);
    setUser((u) => (u ? { ...u, stats_config: data.stats_config } : u));
    return data.stats_config;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, updateStatsConfig }),
    [user, loading, login, logout, refresh, updateStatsConfig]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
